import { ModalCloseSVG } from "@/assets/svg";
import React, { ReactNode, useEffect, useRef } from "react";

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  width?: string;
  showCloseButton?: boolean;
  footer?: ReactNode;
  contentClassName?: string;
  closeOnOutsideClick?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  children,
  width = "500px",
  showCloseButton = true,
  contentClassName,
  closeOnOutsideClick = false,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Close modal when clicking outside (only if closeOnOutsideClick is true)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        closeOnOutsideClick &&
        modalRef.current &&
        !modalRef.current.contains(event.target as Node)
      ) {
        onClose();
      }
    };

    // Handle escape key press (only if closeOnOutsideClick is true)
    const handleEscKey = (event: KeyboardEvent) => {
      if (closeOnOutsideClick && event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscKey);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscKey);
    };
  }, [isOpen, onClose, closeOnOutsideClick]);

  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: "rgba(107, 114, 128, 0.5)" }}
    >
      <div
        ref={modalRef}
        className="bg-white shadow-lg rounded-[10px]"
        style={{ width }}
      >
        {/* Modal header */}
        <div className="flex items-center justify-between px-6 py-4 border-b rounded-t-[10px] border-gray-200 bg-[#F9FBFF]">
          <h2 className="text-lg font-medium text-gray-900">{title}</h2>
          {showCloseButton && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 cursor-pointer"
            >
              <ModalCloseSVG />
            </button>
          )}
        </div>

        {/* Modal body */}
        <div className={`${contentClassName || "px-6 py-4"} rounded-b-[10px]`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default Modal;
